import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { GraduationCap, Languages, Loader2, Volume2, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useEnglishVocab } from '../../hooks/useEnglishVocab'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import { useTextTranslation } from '../../hooks/useTextTranslation'
import EnglishVocabAddSheet from './EnglishVocabAddSheet'

// Global floating "Speak + Save" pill that appears whenever the user
// selects text anywhere in the app. Mounted once at the App shell so
// it doesn't have to be wired per-page.
//
// Two actions, side by side:
//   - 🔊 Speak: TTS the selection in English. For a learner, hearing
//     the word once before saving is a useful tap; this avoids a
//     trip into the vocab card.
//   - 🎓 Save: opens EnglishVocabAddSheet pre-filled with the
//     selected word + surrounding sentence. AI-enriches on save like
//     the article-page flow.
//
// Pinned to bottom-center of the viewport rather than anchored to
// the selection rect — iOS Safari's native menu sits at/around the
// selection, and any anchored UI fights it. A fixed bottom-center
// FAB never collides regardless of where iOS renders its menu.
//
// Owner-only (the rest of the English module is owner-only too).
// Excludes selections inside <input> / <textarea> / contenteditable
// elements so editing text fields doesn't trigger the pill.
const EXCLUDE_TAG = new Set(['INPUT', 'TEXTAREA'])

function isInsideEditable(node) {
  let el = node
  while (el && el !== document.body) {
    if (el.nodeType === 1) {
      if (EXCLUDE_TAG.has(el.tagName)) return true
      if (el.isContentEditable) return true
      if (el.dataset?.noSelectionAction === 'true') return true
    }
    el = el.parentNode
  }
  return false
}

export default function EnglishSelectionAction() {
  const { t } = useTranslation()
  const location = useLocation()
  const { user } = useAuth()
  const ownerGate = hasOwnerAccess(user)

  const [state, setState] = useState({ visible: false, word: '', context: '' })
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetSeed, setSheetSeed] = useState({ word: '', context: '' })
  const [saving, setSaving] = useState(false)
  // Translation result panel state. Shown above the pill row when
  // populated; cleared whenever the selection changes so a fresh
  // selection doesn't carry over stale text. translationTarget
  // tracks the language we asked MS to translate TO, so the panel
  // can render the result with the right direction (rtl/ltr) even
  // though the UI locale may differ.
  const [translationOpen, setTranslationOpen] = useState(false)
  const [translationTarget, setTranslationTarget] = useState('ar')

  const tts = useTextToSpeech()
  const vocab = useEnglishVocab()
  const addVocab = vocab.add
  const translator = useTextTranslation()
  const { translate, reset: resetTranslation } = translator

  useEffect(() => {
    if (!ownerGate) return undefined
    if (typeof document === 'undefined') return undefined
    if (sheetOpen) return undefined

    let pending = null

    const compute = () => {
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return null
      const text = sel.toString().trim()
      if (!text) return null
      // Skip selections inside editable fields — the user is composing,
      // not consuming. Also gives `[data-no-selection-action]` as an
      // opt-out hook for any future surface that wants to disable the
      // pill locally (e.g. the debug overlay's log viewer).
      const range = sel.getRangeAt(0)
      const node = range.commonAncestorContainer
      if (isInsideEditable(node)) return null
      // Cap word length and extract a one-sentence context from the
      // surrounding text so the saved row carries a usable example.
      const anchorText = sel.anchorNode?.textContent || ''
      const sentences = anchorText.split(/(?<=[.!?؟])\s+/)
      const ctx = (sentences.find((s) => s.includes(text)) || anchorText.slice(0, 200))
        .trim()
        .slice(0, 500)
      return {
        word: text.slice(0, 120),
        context: ctx,
      }
    }

    const schedule = () => {
      clearTimeout(pending)
      // 120ms lets iOS render its own selection bubble first so the
      // pill doesn't flash on a quick tap that doesn't actually produce
      // a selection.
      pending = setTimeout(() => {
        const next = compute()
        if (next) setState({ visible: true, ...next })
        else setState((s) => ({ ...s, visible: false }))
      }, 120)
    }

    const onPointerUp = () => schedule()
    const onSelectionChange = () => {
      const sel = window.getSelection()
      if (!sel || !sel.toString().trim()) {
        setState((s) => ({ ...s, visible: false }))
        // Clearing the selection also tears down any in-flight
        // translation panel so it doesn't linger after the pill
        // is gone.
        setTranslationOpen(false)
        resetTranslation()
      }
    }

    document.addEventListener('pointerup', onPointerUp)
    document.addEventListener('selectionchange', onSelectionChange)
    return () => {
      clearTimeout(pending)
      document.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('selectionchange', onSelectionChange)
    }
  }, [ownerGate, sheetOpen, resetTranslation])

  // Map the current route to a source_kind for the vocab row. Keeps
  // the per-feed badges working (a word saved from /tech still shows
  // "Tech" on its card).
  const sourceKindForPath = useCallback(() => {
    const p = location.pathname
    if (p.startsWith('/social')) return 'social'
    if (p.startsWith('/tech')) return 'tech'
    if (p.startsWith('/news')) return 'news'
    if (p.startsWith('/english')) return 'manual'
    return 'manual'
  }, [location.pathname])

  const handleSpeak = useCallback(() => {
    if (!tts.supported || !state.word) return
    tts.play(state.word, 'en')
  }, [tts, state.word])

  const handleTranslate = useCallback(() => {
    if (!state.word) return
    // Flip to the opposite language based on what the user selected,
    // not on the UI locale. Detect script: if the selection contains
    // any Arabic-block characters, translate to English; otherwise
    // (Latin / other) translate to Arabic. Matches the owner's mental
    // model — they want the OTHER language regardless of UI locale,
    // because translating AR → AR or EN → EN is pointless.
    const looksArabic = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/.test(state.word)
    const target = looksArabic ? 'en' : 'ar'
    setTranslationTarget(target)
    setTranslationOpen(true)
    translate(state.word, target)
  }, [state.word, translate])

  const handleCloseTranslation = useCallback(() => {
    setTranslationOpen(false)
    resetTranslation()
  }, [resetTranslation])

  const handleSaveOpen = useCallback(() => {
    setSheetSeed({ word: state.word, context: state.context })
    setSheetOpen(true)
    setState((s) => ({ ...s, visible: false }))
    // Clear the iOS selection bubble so it doesn't sit on top of the
    // modal we're about to open.
    try { window.getSelection()?.removeAllRanges?.() } catch { /* noop */ }
  }, [state.word, state.context])

  const handleSheetSave = useCallback(async (payload) => {
    setSaving(true)
    try {
      return await addVocab(payload)
    } finally {
      setSaving(false)
    }
  }, [addVocab])

  if (!ownerGate) return null
  if (typeof document === 'undefined') return null

  // Pinned above the bottom tab bar (≈64px tall) and the iPhone
  // home-indicator gesture bar via env(safe-area-inset-bottom).
  return createPortal(
    <>
      {state.visible && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
            transform: 'translateX(-50%)',
            zIndex: 60,
            maxWidth: 'calc(100vw - 32px)',
            width: 'max-content',
            pointerEvents: 'none',
          }}
        >
          {/* Translation result panel — sits ABOVE the pill row when
              the user taps Translate. Auto-clears whenever the
              selection changes. */}
          {translationOpen && (
            <div
              className="mb-2 rounded-2xl bg-card border border-border shadow-lg px-3 py-2 animate-in fade-in slide-in-from-bottom-1 duration-150"
              style={{ pointerEvents: 'auto', maxWidth: 'calc(100vw - 32px)' }}
              dir="auto"
            >
              <div className="flex items-start gap-2">
                <Languages className="w-3.5 h-3.5 text-app-primary-500 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  {translator.loading ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground" dir="auto">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>{t('english.vocab.translating')}</span>
                    </div>
                  ) : translator.error ? (
                    <p className="text-xs text-red-500 break-words" dir="auto">
                      {translator.error}
                    </p>
                  ) : translator.translation ? (
                    <p
                      className="text-sm text-foreground leading-relaxed break-words [overflow-wrap:anywhere]"
                      dir={translationTarget === 'ar' ? 'rtl' : 'ltr'}
                    >
                      {translator.translation}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCloseTranslation}
                  aria-label={t('english.vocab.closeTranslation')}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-150 flex-wrap justify-center">
            {/* Speak — TTS the selection. Disabled when TTS is
                unsupported (rare; iOS Safari has it). */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSpeak}
              disabled={!tts.supported}
              aria-label={t('english.vocab.speakSelection')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-foreground text-background text-sm font-semibold shadow-lg active:scale-95 transition-transform disabled:opacity-50"
              style={{ pointerEvents: 'auto' }}
              dir="auto"
            >
              <Volume2 className="w-4 h-4 flex-shrink-0" />
              <span>{t('english.vocab.speakSelection')}</span>
            </button>
            {/* Translate — MS Translator → opposite language. Result
                shown in the panel above the pill row. */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleTranslate}
              aria-label={t('english.vocab.translateSelection')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-app-primary-500/15 text-app-primary-500 border border-app-primary-500/30 text-sm font-semibold shadow-lg active:scale-95 transition-transform"
              style={{ pointerEvents: 'auto' }}
              dir="auto"
            >
              <Languages className="w-4 h-4 flex-shrink-0" />
              <span>{t('english.vocab.translateSelection')}</span>
            </button>
            {/* Save — opens the vocab sheet pre-filled. The truncated
                preview lets the user verify what they selected before
                committing. */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSaveOpen}
              className="inline-flex items-center gap-1.5 ps-3 pe-3.5 py-2 rounded-full bg-app-primary-500 text-white text-sm font-semibold shadow-lg active:scale-95 transition-transform"
              style={{ pointerEvents: 'auto' }}
              dir="auto"
            >
              <GraduationCap className="w-4 h-4 flex-shrink-0" />
              <span>{t('english.vocab.saveFromSelection')}</span>
            </button>
          </div>
        </div>
      )}
      <EnglishVocabAddSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSave={handleSheetSave}
        saving={saving}
        initialWord={sheetSeed.word}
        initialContext={sheetSeed.context}
        sourceKind={sourceKindForPath()}
      />
    </>,
    document.body,
  )
}
